import { TEAMS_PATHS } from '../../../productRouting/legacyProductAuth';
import { useAfterLaunch } from '../../../productRouting/releaseConfig';
import { ROUTORA_PRO } from './routoraPro';
import usePersonalPlanAction from './usePersonalPlanAction';

// The plan cards shared by the homepage pricing section and /pricing.
//
// Before launch they show the previous website's personal plan, which its own
// checkout still sells. After launch (public and holding) the personal plan is
// Routora Pro at the app's offer. Routora Team keeps its own product and
// sign-in, and never claims to include the personal plan. In every mode it
// takes no new subscriptions: its next version is coming soon, and Contact
// sales leads to the waitlist. So the personal plan is the featured card.

const TEAMS_PLAN = {
  title: 'Routora Team',
  description: 'For teams that plan, dispatch, and manage members from one console.',
  icon: 'groups',
  iconTone: 'blue',
  price: 'Coming soon',
  billing: 'A new Routora Team is on the way',
  href: TEAMS_PATHS.sales,
  ctaLabel: 'Contact sales',
  buttonVariant: 'outlined',
  featuresHeading: 'Plan and dispatch for your whole team:',
  features: [
    'Dispatch routes to member mobile apps',
    'Balanced route distribution across members',
    'Route history and saved itineraries',
    'Join the waitlist for launch updates',
  ],
};

function previousPersonalPlan(onClick) {
  return {
    title: 'Personal Workspace',
    description: 'For individuals, couriers, and small operators optimizing routes every day.',
    icon: 'person',
    iconTone: 'green',
    price: '$14.49',
    priceSuffix: '/ month',
    billing: 'Billed annually at $173.99',
    featured: true,
    onClick,
    ctaLabel: 'Start free 7-day trial',
    buttonVariant: 'filled',
    featuresHeading: 'Optimize up to 100 stops per route',
    features: [
      'Import stops from Excel, CSV, or clipboard',
      'Plan on web, navigate on mobile',
      'Works with Google Maps, Apple Maps & Waze',
      'Download and share itineraries',
    ],
  };
}

function routoraProPlan(onClick) {
  return {
    title: ROUTORA_PRO.name,
    description: 'For individuals who plan and run their own routes, at a desk and in the field.',
    icon: 'person',
    iconTone: 'green',
    featured: true,
    price: ROUTORA_PRO.price,
    priceSuffix: ROUTORA_PRO.priceSuffix,
    billing: ROUTORA_PRO.trial,
    onClick,
    ctaLabel: 'Start free trial',
    buttonVariant: 'filled',
    featuresHeading: ROUTORA_PRO.allowance,
    features: [...ROUTORA_PRO.features],
  };
}

const PREVIOUS_PRICING = {
  label: 'Plans & pricing',
  title: 'One platform, two workspaces.',
  description:
    'Plan and dispatch across your whole team with Routora Team, or optimize solo with Personal Workspace.',
  includedItems: ['Web, iOS & Android', 'Unlimited optimizations', 'Works with your navigation app'],
};

const LAUNCH_PRICING = {
  label: 'Plans & pricing',
  title: 'Two ways to use Routora.',
  description:
    'Routora Pro is one subscription for planning on the web and working from your phone. ' +
    'Routora Team plans and dispatches routes for a whole crew.',
  includedItems: ['Web, iOS & Android', 'Works with your navigation app'],
  footnote:
    'Routora Team is a separate product with its own sign-in and billing, and a new version is coming soon. ' +
    'A Teams plan doesn’t include Routora Pro, and Routora Pro doesn’t include Teams.',
};

/** Section copy and plan cards for the current release mode. */
export default function usePricingPlans() {
  const afterLaunch = useAfterLaunch();
  const choosePersonalPlan = usePersonalPlanAction();

  if (!afterLaunch) {
    return {
      ...PREVIOUS_PRICING,
      plans: [previousPersonalPlan(choosePersonalPlan), TEAMS_PLAN],
    };
  }

  return {
    ...LAUNCH_PRICING,
    plans: [routoraProPlan(choosePersonalPlan), TEAMS_PLAN],
  };
}
